/**
 * La red de Firestore al ir y volver del segundo plano · el punto 10 (4-nov-2026).
 *
 * EL REPORTE: en la app instalada en iPhone (Safari → «Agregar a inicio»), al volver del segundo plano la bandeja
 * no mostraba los mensajes que llegaron mientras estaba fuera; había que cerrar y volver a abrir. Con la app en
 * pantalla sí se actualizaba.
 *
 * LA CAUSA EN EL CÓDIGO (el gancho anterior, `useFirestoreIdleManager`): al ocultarse programaba `disableNetwork`
 * a los 60 s, y al volver sólo encendía si una bandera `networkDisabled` ya estaba en `true` — y la bandera se
 * ponía DESPUÉS del `await disableNetwork`. iOS congela la app en segundo plano: el temporizador no corre, y al
 * volver vence de golpe. Si corre antes que el `visibilitychange`, el apagado arranca, el «visible» mira la
 * bandera todavía en `false` y no enciende, y el apagado termina después: la red queda APAGADA con la app en
 * pantalla, y los oyentes sólo ven la caché. Hasta el siguiente ir y volver, o cerrar la app.
 *
 * Y AUNQUE NO SE HAYA APAGADO NADA: iOS corta las conexiones de una app congelada, y el SDK puede seguir
 * creyéndose conectado un buen rato. Por eso, tras un rato fuera, volver RECONECTA (apagar + encender), no sólo
 * enciende: es lo que obliga al SDK a abrir un canal nuevo y a pedir lo que cambió (con el token de reanudación,
 * sin releer la consulta entera si estuvo fuera menos de 30 min).
 *
 * LAS DOS REGLAS QUE LO CIERRAN:
 *   1. La decisión de apagar se ANOTA en el momento (síncrona), no tras el `await`: quien vuelve la ve siempre.
 *   2. Volver NUNCA deja la red apagada: o reconecta, o enciende si quedó apagada por inactividad.
 * Más una tercera de higiene: el temporizador de inactividad mira si la app sigue oculta AL VENCER, y uno de un
 * ciclo anterior (generación vieja) no hace nada.
 *
 * Función PURA + un gestor con las dependencias inyectadas, en CommonJS, para probarlo desde
 * `functions/__tests__` (los tests de `src/` no corren en ninguna puerta). El gancho que lo monta es
 * `src/hooks/useFirestoreIdleManager.js`.
 */

/** Oculta tanto tiempo, se apaga la red (ahorro: una pestaña oculta en escritorio no sigue recibiendo). */
const INACTIVIDAD_MS = 60000;
/** Fuera al menos esto, volver reconecta. Un vistazo al centro de notificaciones no. */
const UMBRAL_FUERA_MS = 3000;
/** Cada cuánto late la página; un salto mayor que `SALTO_DE_LATIDO_MS` es que estuvo congelada. */
const LATIDO_MS = 2000;
const SALTO_DE_LATIDO_MS = 6000;
/** Volver dispara visible + pageshow + focus casi juntos: una sola reconexión. */
const ENTRE_RECONEXIONES_MS = 2000;
/**
 * El latido también salta con la página visible si el hilo se bloquea (un `confirm` abierto, una tarea larga) o si
 * el navegador frena los temporizadores de una ventana tapada: cuando el salto del latido es la ÚNICA evidencia, a
 * lo más una reconexión cada 30 s. Con la ocultación anotada, la vuelta es real y el freno es el de siempre: al
 * descongelarse el latido puede llegar antes que el `visible`, y con 30 s se comía la vuelta de quien fue a
 * WhatsApp y volvió en 25 s (segunda revisión independiente, 🟠1).
 */
const ENTRE_RECONEXIONES_POR_LATIDO_MS = 30000;

/**
 * Qué hacer al volver (o al pedirlo la persona).
 *
 * @param {{ tipo: 'visible'|'pageshow'|'focus'|'online'|'latido'|'manual', persisted?: boolean, ahoraMs: number,
 *   ocultoDesde: number|null, ultimoLatido: number|null, apagadaPorInactividad: boolean,
 *   ultimaReconexion: number|null }} e
 * @returns {{ accion: 'reconectar'|'encender'|'nada', motivo: string, fueraMs: number }}
 */
function decidirAlVolver({ tipo, persisted = false, ahoraMs, ocultoDesde, ultimoLatido, apagadaPorInactividad, ultimaReconexion }) {
  // Sin el `visibilitychange` (iOS no siempre lo da), el salto del latido dice que la página estuvo congelada.
  const salto = ultimoLatido != null ? ahoraMs - ultimoLatido : 0;
  const fueraMs = ocultoDesde != null ? ahoraMs - ocultoDesde : (salto >= SALTO_DE_LATIDO_MS ? salto : 0);
  if (tipo === 'manual') return { accion: 'reconectar', motivo: 'manual', fueraMs };
  const motivoParaReconectar = tipo === 'online' ? 'online'
    : (tipo === 'pageshow' && persisted) ? 'pageshow-persistida'
      : fueraMs >= UMBRAL_FUERA_MS ? 'tiempo-fuera'
        : null;
  const freno = tipo === 'latido' && ocultoDesde == null ? ENTRE_RECONEXIONES_POR_LATIDO_MS : ENTRE_RECONEXIONES_MS;
  const reciente = ultimaReconexion != null && ahoraMs - ultimaReconexion < freno;
  if (motivoParaReconectar && !reciente) return { accion: 'reconectar', motivo: motivoParaReconectar, fueraMs };
  // La regla 2: aunque no toque reconectar, la red no se queda apagada con la app en pantalla.
  if (apagadaPorInactividad) return { accion: 'encender', motivo: 'apagada-por-inactividad', fueraMs };
  return { accion: 'nada', motivo: motivoParaReconectar ? 'reconexion-reciente' : 'poco-tiempo-fuera', fueraMs };
}

/**
 * El gestor: guarda el estado y aplica las decisiones sobre `apagar`/`encender` (las de Firestore).
 *
 * @param {{ apagar: () => Promise<void>, encender: () => Promise<void>, esVisible: () => boolean,
 *   ahora?: () => number, programar?: Function, cancelar?: Function,
 *   registrar?: (evento: string, datos: object) => void }} deps
 */
function crearGestorDeRed({
  apagar, encender, esVisible, ahora = () => Date.now(), programar = setTimeout, cancelar = clearTimeout,
  registrar = () => {},
}) {
  let ocultoDesde = null;
  let ultimoLatido = ahora();
  let apagadaPorInactividad = false;
  let ultimaReconexion = null;
  let temporizador = null;
  let generacion = 0;
  // Sin cola propia: el SDK ya aplica `disableNetwork`/`enableNetwork` en el orden de llamada (su AsyncQueue), y
  // toda vuelta termina en un `encender`. Un fallo se registra y no lanza: la decisión siguiente tiene que aplicarse.
  const ejecutar = (paso, etiqueta) => Promise.resolve().then(paso)
    .catch((e) => registrar('error', { paso: etiqueta, error: String((e && e.message) || e).slice(0, 200) }));

  const anularTemporizador = () => {
    generacion += 1;
    if (temporizador != null) cancelar(temporizador);
    temporizador = null;
  };

  function alOcultar() {
    // `pagehide` y `visibilitychange` llegan los dos: cuenta desde el PRIMERO.
    if (ocultoDesde == null) ocultoDesde = ahora();
    anularTemporizador();
    const suya = generacion;
    temporizador = programar(() => {
      temporizador = null;
      // Al VENCER, no al programar: un temporizador de otro ciclo, o con la app ya en pantalla, no apaga.
      if (suya !== generacion || esVisible()) { registrar('apagado-descartado', { generacionVieja: suya !== generacion }); return; }
      apagadaPorInactividad = true;   // regla 1: anotado YA, antes de cualquier await
      ejecutar(apagar, 'apagar');
    }, INACTIVIDAD_MS);
  }

  function alVolver(tipo, { persisted = false } = {}) {
    // Un `online` (o un latido) con la app oculta no es volver: ni reconecta ni cancela el apagado. El tirón sí pasa:
    // un dedo en la pantalla prueba que se ve, aunque iOS diga «hidden» — es el respaldo justo para ese caso.
    if (tipo !== 'manual' && !esVisible()) return { accion: 'nada', motivo: 'oculta', fueraMs: 0, listo: Promise.resolve() };
    anularTemporizador();
    const ahoraMs = ahora();
    const d = decidirAlVolver({ tipo, persisted, ahoraMs, ocultoDesde, ultimoLatido, apagadaPorInactividad, ultimaReconexion });
    ocultoDesde = null;
    ultimoLatido = ahoraMs;
    let listo = Promise.resolve();
    if (d.accion === 'reconectar') {
      ultimaReconexion = ahoraMs;
      apagadaPorInactividad = false;
      // `finally`: si apagar falla, igual se enciende (regla 2); el error de apagar se registra después.
      listo = ejecutar(async () => { try { await apagar(); } finally { await encender(); } }, 'reconectar');
    } else if (d.accion === 'encender') {
      apagadaPorInactividad = false;
      listo = ejecutar(encender, 'encender');
    }
    // `listo` va al registro: quien mide espera al servidor DESPUÉS de reconectar, no desde la decisión.
    if (d.accion !== 'nada') registrar('reconexion', { tipo, ...d, listo });
    return { ...d, listo };
  }

  return {
    alOcultar,
    alVolver,
    /** La persona tiró para actualizar: reconecta siempre. `listo` se resuelve al terminar. */
    reconectarAhora: () => alVolver('manual'),
    /**
     * Al descongelarse, el intervalo vencido puede correr ANTES que cualquier evento: si sólo actualizara la
     * marca, borraría la prueba del salto. Con un salto (o una vuelta aún sin procesar), el latido ES la vuelta;
     * con la app oculta, `alVolver` no hace nada (en escritorio el intervalo sigue, frenado).
     */
    latido: () => {
      const t = ahora();
      if (ocultoDesde != null || t - ultimoLatido >= SALTO_DE_LATIDO_MS) { alVolver('latido'); return; }
      ultimoLatido = t;
    },
    detener: anularTemporizador,
  };
}

module.exports = {
  decidirAlVolver, crearGestorDeRed,
  INACTIVIDAD_MS, UMBRAL_FUERA_MS, LATIDO_MS, SALTO_DE_LATIDO_MS, ENTRE_RECONEXIONES_MS, ENTRE_RECONEXIONES_POR_LATIDO_MS,
};
